import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, Users, Clock, MapPin, Shield, Star, Zap, Mail, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import Logo from '../components/Logo';

export default function Landing() {
  const navigate = useNavigate();

  // JSON-LD Structured Data for SEO
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "Errands Boy",
    "image": "https://errandsboy.online/logo.png",
    "url": "https://errandsboy.online",
    "description": "South Africa's trusted errand service. Parcel delivery, queue assistance, and multi-stop errands across major cities.",
    "areaServed": {
      "@type": "Country",
      "name": "South Africa"
    },
    "priceRange": "R50 - R500",
    "email": "hello@errandsboy.online",
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "reviewCount": "127"
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 to-white">
      {/* SEO Structured Data */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />

      {/* Hero Section */}
      <section className="hero-section">
        <div className="container mx-auto px-4 md:px-8 lg:px-12 py-8">
          <div className="flex items-center justify-between mb-8">
            <Logo size="lg" />
            <div className="flex gap-2">
              <Button
                data-testid="header-login-btn"
                onClick={() => navigate('/login')}
                className="bg-transparent hover:bg-slate-100 text-slate-700 rounded-full px-5 py-2 text-sm font-medium btn-hover"
              >
                Login
              </Button>
              <Button
                data-testid="header-signup-btn"
                onClick={() => navigate('/register')}
                className="bg-slate-900 hover:bg-black text-white rounded-full px-5 py-2 text-sm font-medium btn-hover"
              >
                Sign up
              </Button>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center py-8">
            <div className="md:col-span-6 space-y-6">
              <div className="inline-flex items-center gap-2 bg-yellow-100 text-yellow-800 px-3 py-1.5 rounded-full text-xs font-medium">
                <Zap className="w-3.5 h-3.5" />
                Trusted by 500+ South Africans
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900">
                Errand Service in<br />
                <span className="text-yellow-500">South Africa</span>
              </h1>
              <p className="text-lg text-slate-600 leading-relaxed">
                Skip the queue at Home Affairs, get parcels delivered across town, or handle any multi-stop errand — connect with verified runners in Johannesburg, Cape Town, Durban and beyond.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button
                  data-testid="get-started-btn"
                  onClick={() => navigate('/register')}
                  className="bg-slate-900 hover:bg-black text-white rounded-full px-8 py-3 font-medium shadow-lg btn-hover"
                >
                  Book an Errand
                </Button>
                <Button
                  data-testid="login-btn"
                  onClick={() => navigate('/register')}
                  className="bg-yellow-400 hover:bg-yellow-500 text-slate-900 rounded-full px-8 py-3 font-medium shadow-md btn-hover"
                >
                  Earn as a Runner
                </Button>
              </div>
              <div className="flex items-center gap-6 pt-4">
                <div className="flex items-center gap-1.5">
                  <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  <span className="text-sm font-medium text-slate-700">4.9 rating</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Shield className="w-4 h-4 text-slate-700" />
                  <span className="text-sm font-medium text-slate-700">Verified runners</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Clock className="w-4 h-4 text-slate-700" />
                  <span className="text-sm font-medium text-slate-700">Same-day</span>
                </div>
              </div>
            </div>
            <div className="md:col-span-6 flex items-center justify-center">
              <img
                src="/logo.png"
                alt="Errands Boy - South African errand runner delivering parcels"
                className="w-full max-w-md object-contain"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4 md:px-8 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Errand Services We Offer</h2>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto">From parcel drop-offs to queue standing at government offices — we handle the tasks stealing your time.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <Card data-testid="service-parcel-card" className="bg-white border border-slate-100 shadow-sm rounded-xl p-6 card-hover">
              <div className="bg-yellow-100 w-12 h-12 rounded-full flex items-center justify-center mb-4">
                <Package className="w-6 h-6 text-yellow-600" strokeWidth={1.5} />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Parcel Delivery</h3>
              <p className="text-slate-600">Same-day parcel drop-offs across town. Perfect for online sellers, businesses, and personal deliveries.</p>
            </Card>

            <Card data-testid="service-queue-card" className="bg-white border border-slate-100 shadow-sm rounded-xl p-6 card-hover">
              <div className="bg-slate-900 w-12 h-12 rounded-full flex items-center justify-center mb-4">
                <Users className="w-6 h-6 text-yellow-400" strokeWidth={1.5} />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Queue Standing</h3>
              <p className="text-slate-600">Home Affairs, banks, licensing departments — someone waits so you don't have to.</p>
            </Card>

            <Card data-testid="service-pricing-card" className="bg-white border border-slate-100 shadow-sm rounded-xl p-6 card-hover">
              <div className="bg-yellow-100 w-12 h-12 rounded-full flex items-center justify-center mb-4">
                <MapPin className="w-6 h-6 text-yellow-600" strokeWidth={1.5} />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Multi-Stop Errands</h3>
              <p className="text-slate-600">Combine grocery runs, pharmacy pickups, and post office trips in one booking with transparent per-stop pricing.</p>
            </Card>

            <Card data-testid="service-instant-card" className="bg-white border border-slate-100 shadow-sm rounded-xl p-6 card-hover">
              <div className="bg-slate-900 w-12 h-12 rounded-full flex items-center justify-center mb-4">
                <Clock className="w-6 h-6 text-yellow-400" strokeWidth={1.5} />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Instant Matching</h3>
              <p className="text-slate-600">Post your errand, pay securely, and get matched with a nearby runner — usually within minutes.</p>
            </Card>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-24 bg-slate-50">
        <div className="container mx-auto px-4 md:px-8 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">How Errands Boy Works</h2>
            <p className="text-slate-600 text-lg">Three simple steps to reclaim your time.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { num: '1', title: 'Post Your Errand', desc: 'Describe what needs doing, add stop addresses, and see instant pricing in ZAR.' },
              { num: '2', title: 'Pay Securely', desc: 'Choose Yoco, PayFast, or Ozow — all trusted South African payment gateways.' },
              { num: '3', title: 'Runner Handles It', desc: 'Get real-time updates as your verified runner picks up, delivers, or waits for you.' }
            ].map((step) => (
              <div key={step.num} className="text-center">
                <div className="w-16 h-16 bg-yellow-400 text-slate-900 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                  {step.num}
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">{step.title}</h3>
                <p className="text-slate-600">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4 md:px-8 lg:px-12">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Simple, Transparent Pricing</h2>
            <p className="text-slate-600 text-lg">No hidden fees. Only pay for what you use.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
            <Card data-testid="pricing-parcel" className="bg-white border-2 border-slate-100 rounded-xl p-8 card-hover">
              <Package className="w-8 h-8 text-yellow-500 mb-4" strokeWidth={1.5} />
              <h3 className="text-2xl font-bold text-slate-900 mb-1">Parcel Delivery</h3>
              <p className="text-slate-500 text-sm mb-6">Perfect for drop-offs and pickups</p>
              <div className="flex items-baseline gap-1 mb-6">
                <span className="text-4xl font-bold text-slate-900">R50</span>
                <span className="text-slate-600">base + R25 per stop</span>
              </div>
              <ul className="space-y-2 text-sm text-slate-600">
                <li>✓ Same-day service</li>
                <li>✓ Real-time tracking</li>
                <li>✓ Verified runners</li>
              </ul>
            </Card>

            <Card data-testid="pricing-queue" className="bg-slate-900 border-2 border-slate-900 rounded-xl p-8 card-hover text-white">
              <Users className="w-8 h-8 text-yellow-400 mb-4" strokeWidth={1.5} />
              <h3 className="text-2xl font-bold mb-1">Queue Assistance</h3>
              <p className="text-slate-400 text-sm mb-6">Home Affairs, banks, and more</p>
              <div className="flex items-baseline gap-1 mb-6">
                <span className="text-4xl font-bold text-yellow-400">R80</span>
                <span className="text-slate-300">base + R25 per stop</span>
              </div>
              <ul className="space-y-2 text-sm text-slate-300">
                <li>✓ Save hours of your day</li>
                <li>✓ Call-when-you're-close alerts</li>
                <li>✓ Handle multiple queues</li>
              </ul>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ / SEO Content Section */}
      <section className="py-24 bg-slate-50">
        <div className="container mx-auto px-4 md:px-8 lg:px-12 max-w-4xl">
          <h2 className="text-4xl font-bold text-slate-900 mb-8 text-center">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {[
              { q: "How does Errands Boy work in South Africa?", a: "Errands Boy connects busy South Africans with reliable runners who handle parcel deliveries, queue standing, and multi-stop errands. Post your task, pay in ZAR through Yoco, PayFast, or Ozow, and a nearby runner picks it up." },
              { q: "What cities do you serve?", a: "We currently serve major South African cities including Johannesburg, Cape Town, Pretoria, Durban, Port Elizabeth, and Bloemfontein — with runners active across the country." },
              { q: "How much does an errand cost?", a: "Parcel delivery starts at R50 base + R25 per stop. Queue assistance starts at R80 base + R25 per stop. No hidden fees — see the total before you pay." },
              { q: "Are the runners verified?", a: "Yes. All runners register with their real identity and can be rated by customers after every completed job. High-rated runners get priority job matching." },
              { q: "Can I earn money as a runner?", a: "Absolutely. Sign up as a runner, browse available jobs in your area, and accept the ones that fit your schedule. Great side hustle for students, gig workers, or anyone with free time." }
            ].map((faq, i) => (
              <details key={i} className="bg-white border border-slate-200 rounded-xl p-6 group">
                <summary className="font-medium text-slate-900 cursor-pointer flex items-center justify-between">
                  {faq.q}
                  <span className="text-yellow-500 group-open:rotate-45 transition-transform text-2xl">+</span>
                </summary>
                <p className="text-slate-600 mt-3 leading-relaxed">{faq.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-white">
        <div className="container mx-auto px-4 md:px-8 lg:px-12 max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Get in Touch</h2>
            <p className="text-slate-600 text-lg">Questions, feedback, or partnership ideas? We'd love to hear from you.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <a
              data-testid="contact-email"
              href="mailto:hello@errandsboy.online"
              className="bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-xl p-6 card-hover flex items-center gap-4"
            >
              <div className="w-12 h-12 bg-yellow-400 rounded-full flex items-center justify-center flex-shrink-0">
                <Mail className="w-6 h-6 text-slate-900" strokeWidth={1.5} />
              </div>
              <div>
                <h3 className="font-bold text-slate-900">Email Us</h3>
                <p className="text-slate-600 text-sm">hello@errandsboy.online</p>
              </div>
            </a>

            <a
              data-testid="contact-whatsapp"
              href="https://wa.me/27000000000"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-xl p-6 card-hover flex items-center gap-4"
            >
              <div className="w-12 h-12 bg-slate-900 rounded-full flex items-center justify-center flex-shrink-0">
                <MessageCircle className="w-6 h-6 text-yellow-400" strokeWidth={1.5} />
              </div>
              <div>
                <h3 className="font-bold text-slate-900">WhatsApp</h3>
                <p className="text-slate-600 text-sm">Chat with support</p>
              </div>
            </a>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-slate-900 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-yellow-400/10 rounded-full blur-3xl"></div>
        <div className="container mx-auto px-4 md:px-8 lg:px-12 text-center relative z-10">
          <h2 className="text-4xl font-bold text-white mb-6">Ready to Reclaim Your Time?</h2>
          <p className="text-slate-300 text-lg mb-8 max-w-2xl mx-auto">
            Join hundreds of South Africans already using Errands Boy to skip queues and streamline their day.
          </p>
          <Button
            data-testid="cta-register-btn"
            onClick={() => navigate('/register')}
            className="bg-yellow-400 hover:bg-yellow-500 text-slate-900 rounded-full px-8 py-3 font-bold shadow-lg btn-hover"
          >
            Sign Up Free
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-8 border-t border-slate-800">
        <div className="container mx-auto px-4 md:px-8 lg:px-12 flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-2">
            <img src="/logo.png" alt="Errands Boy" className="h-10 w-10 object-contain" />
            <span className="font-bold text-white">ERRANDS <span className="text-yellow-500">BOY</span></span>
          </div>
          <div className="flex flex-wrap items-center gap-4 text-sm">
            <a href="mailto:hello@errandsboy.online" className="hover:text-yellow-400 transition-colors">hello@errandsboy.online</a>
            <span className="text-slate-600">·</span>
            <span>© 2026 Errands Boy</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
